import { useState, type FormEvent } from 'react';
import { AnimatePresence } from 'motion/react';
import { useAuth } from '@/context/AuthContext';
import {
  Button,
  Input,
  Heading,
  MonoLabel,
  Divider,
  InlineMessage,
  Card,
} from '@/components/rc';
import WineIcon from '@/components/icons/WineIcon';

const AppleLogo = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

export default function LoginPage() {
  const { error, signInWithGoogle, signInWithApple, continueWithEmail } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const disabled = submitting || !email || !password;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await continueWithEmail(email, password, displayName || undefined);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setSubmitting(true);
    try {
      await signInWithGoogle();
    } finally {
      setSubmitting(false);
    }
  };

  const handleAppleSignIn = async () => {
    setSubmitting(true);
    try {
      await signInWithApple();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center bg-[var(--rc-surface-primary)] px-4">
      <Card elevation="raised" padding="standard" className="w-full max-w-[440px]">
        <div className="flex flex-col items-center gap-6">
          {/* Brand */}
          <div className="flex flex-col items-center gap-2">
            <WineIcon size={48} />
            <Heading scale="title" colour="accent-pink" align="centre">
              RAVE CAVE
            </Heading>
            <MonoLabel size="label" colour="ghost" className="w-auto">
              Your personal wine cellar
            </MonoLabel>
          </div>

          {/* Google sign-in */}
          <Button
            variantType="Primary"
            label={submitting ? 'Connecting...' : 'Continue with Google'}
            disabled={submitting}
            onClick={handleGoogleSignIn}
            className="w-full"
          />

          {/* Apple sign-in */}
          <button
            type="button"
            onClick={handleAppleSignIn}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2.5 h-[var(--rc-button-height-mobile)] px-[var(--rc-button-padding-h-filled)] rounded-[var(--rc-button-radius)] bg-white text-black font-semibold font-[family-name:var(--rc-font-body)] text-sm transition-colors duration-200 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <AppleLogo />
            {submitting ? 'Connecting...' : 'Continue with Apple'}
          </button>

          {/* Divider row */}
          <div className="flex items-center gap-3 w-full">
            <Divider className="flex-1" />
            <MonoLabel size="label" colour="ghost" className="w-auto">
              or
            </MonoLabel>
            <Divider className="flex-1" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-3 w-full">
            <Input
              typeVariant="Text"
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              state={submitting ? 'Disabled' : 'Default'}
              autoComplete="email"
            />

            <Input
              typeVariant="Text"
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              state={submitting ? 'Disabled' : 'Default'}
              autoComplete="current-password"
            />

            <Input
              typeVariant="Text"
              placeholder="Display name (new accounts)"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              state={submitting ? 'Disabled' : 'Default'}
              autoComplete="name"
            />

            <Button
              type="submit"
              variantType="Primary"
              label={submitting ? 'Signing In...' : 'Continue with Email'}
              disabled={disabled}
              className="w-full"
            />
          </form>

          {/* Error message */}
          <AnimatePresence>
            {error && (
              <InlineMessage tone="error" message={error} />
            )}
          </AnimatePresence>

          {/* Learn more */}
          <a
            href="/welcome"
            className="font-mono text-[11px] uppercase tracking-wider text-[var(--rc-text-secondary)] hover:text-[var(--rc-text-primary)] transition-colors"
          >
            Learn more about Rave Cave &rarr;
          </a>
        </div>
      </Card>
    </div>
  );
}
